import type { ProductVariant } from './productVariants';

const uniqueSorted = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const value = raw.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.sort((a, b) => a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }));
};

/**
 * Detecta si las variantes tienen exactamente 2 ejes y se pueden renderizar
 * como grid 2D. Los ejes se detectan dinámicamente de Object.keys(attributes).
 */
export function canRenderVariantGrid(variants: ProductVariant[]): boolean {
  if (variants.length < 2) return false;
  const axisSet = new Set<string>();
  for (const v of variants) {
    if (!v.attributes) continue;
    for (const k of Object.keys(v.attributes)) {
      if (v.attributes[k]?.trim()) axisSet.add(k);
    }
  }
  if (axisSet.size !== 2) return false;
  const axes = Array.from(axisSet);
  const eligible = variants.filter(
    (v) => v.attributes?.[axes[0]]?.trim() && v.attributes?.[axes[1]]?.trim(),
  );
  return eligible.length >= 2 && eligible.length === variants.length;
}

export type VariantGridLayout = {
  rowAxis: string;
  colAxis: string;
  rows: string[];
  cols: string[];
  cellByKey: Map<string, ProductVariant>;
};

export function buildVariantGridLayout(variants: ProductVariant[]): VariantGridLayout | null {
  if (!canRenderVariantGrid(variants)) return null;

  const axisSet = new Set<string>();
  for (const v of variants) {
    if (!v.attributes) continue;
    for (const k of Object.keys(v.attributes)) {
      if (v.attributes[k]?.trim()) axisSet.add(k);
    }
  }
  const axes = Array.from(axisSet);
  // Primer eje = filas, segundo = columnas (orden alfabético para estabilidad).
  axes.sort();
  const [rowAxis, colAxis] = axes;

  const rows = uniqueSorted(variants.map((v) => v.attributes?.[rowAxis] ?? ''));
  const cols = uniqueSorted(variants.map((v) => v.attributes?.[colAxis] ?? ''));

  const cellByKey = new Map<string, ProductVariant>();
  for (const variant of variants) {
    const rowVal = variant.attributes?.[rowAxis]?.trim() ?? '';
    const colVal = variant.attributes?.[colAxis]?.trim() ?? '';
    if (!rowVal || !colVal) continue;
    cellByKey.set(`${rowVal}|${colVal}`, variant);
  }

  return { rowAxis, colAxis, rows, cols, cellByKey };
}
